import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router, ActivatedRoute } from '@angular/router';
import {
  PoBreadcrumb,
  PoPageAction,
  PoPageFilter,
  PoPageListComponent,
  PoTableAction,
  PoTableColumn,
  PoTableColumnSpacing,
  PoTableComponent,
  PoDialogService,
  PoModalComponent
} from '@po-ui/ng-components';
import { SchemaBuilderService } from '../../core/menu/schema-builder.service';
import { environment } from '../../../environments/environment';
import { ShortcutService } from '../../core/shortcut/shortcut.service';
import { Subscription, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

@Component({
  selector: 'app-dispositivos',
  templateUrl: './dispositivos.component.html',
  styleUrl: './dispositivos.component.css',
  standalone: false
})
export class DispositivosComponent implements OnInit, OnDestroy {

  @ViewChild('dynamicTable', { static: true }) dynamicTable!: PoTableComponent;
  @ViewChild('pageListComponent', { static: true }) pageListComponent!: PoPageListComponent;
  @ViewChild('modalDeviceDetails') modalDeviceDetails!: PoModalComponent;
  @ViewChild('modalDeviceLogs') modalDeviceLogs!: PoModalComponent;

  loading = false;
  editClient: any;
  isHideLoading = true;
  selectedDevice: any = null;
  isHideLoadingDeviceDetails = true;
  selectedDeviceForLogs: any = null;
  deviceLogs: any[] = [];
  deviceLogsPage: number = 1;
  deviceLogsPageSize: number = 50;
  deviceLogsHasMore: boolean = false;
  deviceLogsDeviceId: string = '';
  isHideLoadingDeviceLogs = true;
  deviceLogDateRange: any = null;
  deviceLogsSearchText: string = '';
  deviceLogsAll: any[] = []; // Armazena todos os logs carregados (sem filtro)
  items: any[] = [];
  columns: Array<PoTableColumn> = [];
  poTableColumnSpacing: PoTableColumnSpacing = PoTableColumnSpacing.Small;
  moreItens: boolean = false;
  page: number = 1;
  pageSize: number = 15;
  filter: string = '';
  disclaimerGroup: any;
  private disclaimers: any[] = [];
  private shortcutSubscription!: Subscription;
  private deviceLogsSearchSubject = new Subject<string>();
  private deviceLogsSearchSubscription?: Subscription;
  public readonly filterSettings: PoPageFilter = {
    action: this.filterAction.bind(this),
    placeholder: 'Pesquisar'
  };
  readonly serviceApi = environment.apiUrl + '/v1/devices';

  readonly breadcrumb: PoBreadcrumb = {
    items: [{ label: 'Home', link: '/home' }, { label: 'Controle de Acesso' }, { label: 'Dispositivos' }]
  };

  readonly pageActions: Array<PoPageAction> = [
    {
      label: 'Novo Dispositivo',
      action: () => this.router.navigate(['/devices-edit']),
      icon: 'an an-plus'
    },
    {
      label: 'Atualizar',
      action: () => this.resetTable(),
      icon: 'an an-arrows-clockwise'
    }
  ];

  readonly actions: PoTableAction[] = [
    {
      label: 'Editar Registro',
      action: this.edit.bind(this),
      icon: 'an an-pencil',
      type: 'default'
    },
    {
      label: 'Deletar Registro',
      action: this.confirmDelete.bind(this),
      icon: 'an an-trash',
      type: 'danger',
      separator: true
    },
    {
      label: 'Visualizar Detalhes',
      action: this.viewDeviceDetails.bind(this),
      icon: 'po-icon po-icon-eye',
    },
    {
      label: 'Registro de Acesso',
      action: this.viewDeviceLogs.bind(this),
      icon: 'po-icon po-icon-list',
      type: 'default',
      separator: true
    }
  ];

  constructor(private readonly schemaBuilderService: SchemaBuilderService,
    private readonly route: ActivatedRoute,
    private readonly poDialogService: PoDialogService,
    private readonly shortcutService: ShortcutService,
    private readonly router: Router,
    private readonly http: HttpClient) {
    this.schemaBuilderService.getTableSchema('device').subscribe(data => {
      const transformedColumns = this.transformTableSchema(data as any[]);

      // Garante que a coluna lockType seja transformada - verifica várias variações do nome
      const lockTypeVariations = ['locktype', 'lock_type', 'lock-type', 'tipo_fechadura', 'tipofechadura'];
      const lockTypeColumnIndex = transformedColumns.findIndex(col => {
        const propLower = col.property?.toLowerCase() || '';
        return lockTypeVariations.some(variation => propLower.includes(variation) || propLower === variation);
      });

      if (lockTypeColumnIndex >= 0) {
        transformedColumns[lockTypeColumnIndex] = {
          ...transformedColumns[lockTypeColumnIndex],
          type: 'label',
          labels: [
            { value: 1, color: 'color-08', label: 'Fechadura comum', textColor: 'white' },
            { value: 0, color: 'color-11', label: 'Fechadura magnética', textColor: 'white' }
          ]
        };
      }

      this.columns = [
        ...transformedColumns,
        {
          property: 'status',
          label: 'Status',
          type: 'label',
          width: '100px',
          labels: [
            { value: 'Online', color: 'color-10', label: 'Online', textColor: 'white' },
            { value: 'Offline', color: 'color-07', label: 'Offline', textColor: 'white' }
          ]
        }
      ];
    });
    this.shortcutSubscription = this.shortcutService.getShortcuts().subscribe((event: KeyboardEvent) => {
      if (event.ctrlKey && event.key === 'b') {
        event.preventDefault();
        this.router.navigate(['/devices-edit']);
      } else if (event.ctrlKey && event.key === 'f') {
        event.preventDefault();
        this.pageListComponent.filterInput.nativeElement.focus();
      }
    });
  }

  private transformTableSchema(schema: any[]): PoTableColumn[] {
    return schema
      .filter(field => field.visible !== false && field.showInGrid !== false)
      .sort((a, b) => (a.order || 0) - (b.order || 0))
      .map(field => {
        const transformedColumn: PoTableColumn = {
          property: field.property,
          label: field.label,
        };

        // Detecta campo de tipo de fechadura (lockType, doorType, fechadura, etc.)
        const lockTypeFields = ['locktype', 'lock_type', 'lock-type', 'doortype', 'fechadura', 'tipofechadura', 'tipo_fechadura', 'lock', 'doorlock'];
        const fieldPropertyLower = field.property?.toLowerCase() || '';

        // Verifica se o campo é de tipo de fechadura (match exato ou parcial)
        const isLockTypeField = lockTypeFields.some(lockField =>
          fieldPropertyLower === lockField || fieldPropertyLower.includes(lockField)
        );

        if (isLockTypeField) {
          transformedColumn.type = 'label';
          transformedColumn.labels = [
            { value: 1, color: 'color-08', label: 'Fechadura comum', textColor: 'white' },
            { value: 0, color: 'color-11', label: 'Fechadura magnética', textColor: 'white' }
          ];
        } else if (field.type) {
          const typeLower = field.type.toLowerCase();
          if (typeLower === 'datetime' || typeLower === 'date') {
            transformedColumn.type = 'date';
            if (field.format) {
              transformedColumn.format = field.format;
            }
          } else if (typeLower === 'number' || typeLower === 'int' || typeLower === 'integer') {
            transformedColumn.type = 'number';
          } else if (typeLower === 'boolean' || typeLower === 'bool') {
            transformedColumn.type = 'boolean';
            if (field.booleanTrue || field.booleanFalse) {
              transformedColumn.boolean = {
                trueLabel: field.booleanTrue || 'Sim',
                falseLabel: field.booleanFalse || 'Não'
              };
            }
          }
        }

        if (field.width) {
          transformedColumn.width = field.width;
        }

        return transformedColumn;
      });
  }

  ngOnInit(): void {
    this.disclaimerGroup = {
      title: 'Filtros:',
      disclaimers: [],
      change: this.onChangeDisclaimer.bind(this),
      remove: this.onClearDisclaimer.bind(this)
    };
    this.loadMore();
  }

  ngOnDestroy(): void {
    if (this.shortcutSubscription) {
      this.shortcutSubscription.unsubscribe();
    }
    if (this.deviceLogsSearchSubscription) {
      this.deviceLogsSearchSubscription.unsubscribe();
    }
  }

  populateDisclaimers(filters: Array<any>) {
    const property = filters.length > 1 ? 'advanced' : 'search';
    this.disclaimers = filters.map(value => ({ value, property }));

    if (this.disclaimers && this.disclaimers.length > 0) {
      this.disclaimerGroup.disclaimers = [...this.disclaimers];
    } else {
      this.disclaimerGroup.disclaimers = [];
    }
  }

  filterAction(labelFilter: string | Array<string>) {
    const filter = typeof labelFilter === 'string' ? [labelFilter] : [...labelFilter];
    this.populateDisclaimers(filter);
    this.filter = filter.toString();
    this.loadMore(true);
  }

  confirmDelete(item: any) {
    this.poDialogService.confirm({
      title: 'Exclus\u00e3o de Registros',
      message: `Deseja realmente excluir o item selecionado?`,
      confirm: () => this.remove(item)
    });
  }

  remove(item: any) {
    this.isHideLoading = false;
    this.http.delete(`${this.serviceApi}/${item.id}`).subscribe(() => {
      this.dynamicTable.removeItem(item);
      this.isHideLoading = true;
    }, () => {
      this.isHideLoading = true;
    });
  }

  edit(item: any) {
    this.router.navigate(['/devices-edit', item.id]);
  }

  viewDeviceDetails(item: any) {
    if (!item?.id) {
      this.poDialogService.alert({
        title: 'Erro',
        message: 'Dispositivo não possui ID válido.'
      });
      return;
    }

    this.isHideLoadingDeviceDetails = false;
    this.selectedDevice = null;

    this.http.get<any>(`${this.serviceApi}/${item.id}`).subscribe({
      next: (device) => {
        this.selectedDevice = {
          ...device,
          createdAtFormatted: this.formatDate(device.createdAt),
          updatedAtFormatted: this.formatDate(device.updatedAt)
        };
        this.isHideLoadingDeviceDetails = true;
        this.modalDeviceDetails.open();
      },
      error: (err) => {
        this.isHideLoadingDeviceDetails = true;
        this.poDialogService.alert({
          title: 'Erro',
          message: 'Não foi possível carregar os detalhes do dispositivo.'
        });
      }
    });
  }

  private formatDate(date: string | Date | null | undefined): string {
    if (!date) return 'N/A';
    try {
      const d = typeof date === 'string' ? new Date(date) : date;
      // Subtrai 3 horas para converter de UTC para horário do Brasil (UTC-3)
      const brazilTime = new Date(d.getTime() - 3 * 60 * 60 * 1000);
      return brazilTime.toLocaleString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      });
    } catch {
      return String(date);
    }
  }

  /** Retorna 'Online' se UpdatedAt (ou CreatedAt) está a menos de 2h em UTC; caso contrário 'Offline' */
  getDeviceStatus(item: any): 'Online' | 'Offline' {
    const dateStr = item?.updatedAt ?? item?.createdAt;
    if (!dateStr) return 'Offline';
    const updatedAt = new Date(dateStr).getTime();
    const now = Date.now();
    const twoHoursMs = 2 * 60 * 60 * 1000;
    return (now - updatedAt) < twoHoursMs ? 'Online' : 'Offline';
  }

  /** Mapeia o valor de fechadura: 1 = Fechadura comum, 0 = Fechadura magnética */
  private mapLockType(item: any): any {
    const lockTypeFields = ['lockType', 'locktype', 'lock_type', 'lock-type', 'doorType', 'fechadura', 'tipoFechadura', 'tipo_fechadura', 'lock', 'doorLock'];
    const mappedItem = { ...item };

    // Procura por qualquer campo que contenha variações de lockType
    Object.keys(mappedItem).forEach(key => {
      const keyLower = key.toLowerCase();
      const isLockTypeField = lockTypeFields.some(lockField =>
        keyLower === lockField.toLowerCase() || keyLower.includes(lockField.toLowerCase())
      );

      if (isLockTypeField && mappedItem[key] !== undefined && mappedItem[key] !== null && mappedItem[key] !== '') {
        // Garante que o valor seja número (1 ou 0)
        const numValue = Number(mappedItem[key]);
        if (!isNaN(numValue)) {
          mappedItem[key] = numValue;
        }
      }
    });

    return mappedItem;
  }

  resetTable() {
    this.loadMore(true);
  }

  onChangeDisclaimer(disclaimers: any) {
    this.disclaimers = disclaimers;
    this.loadMore(true);
  }

  onClearDisclaimer(disclaimers: any) {
    if (disclaimers.removedDisclaimer.property === 'search') {
      this.pageListComponent.clearInputSearch();
    }
    this.disclaimers = [];
    this.filter = '';
    this.loadMore(true);
  }

  loadMore(reset: boolean = false): void {
    this.isHideLoading = false;
    if (reset) {
      this.page = 1;
      this.http.get<any>(`${this.serviceApi}?page=${this.page}&pageSize=${this.pageSize}&search=${this.filter}`).subscribe((data) => {
        this.items = [];
        const mapped = (data.items || []).map((item: any) => {
          const mappedItem = this.mapLockType(item);
          return { ...mappedItem, status: this.getDeviceStatus(mappedItem) };
        });
        this.items = [...this.items, ...mapped];
        this.moreItens = data.hasNext;
        this.page++;
        this.isHideLoading = true;
      }, () => {
        this.isHideLoading = true;
      });
    } else {
      this.http.get<any>(`${this.serviceApi}?page=${this.page}&pageSize=${this.pageSize}&search=${this.filter}`).subscribe((data) => {
        const mapped = (data.items || []).map((item: any) => {
          const mappedItem = this.mapLockType(item);
          return { ...mappedItem, status: this.getDeviceStatus(mappedItem) };
        });
        this.items = [...this.items, ...mapped];
        this.moreItens = data.hasNext;
        this.page++;
        this.isHideLoading = true;
      }, () => {
        this.isHideLoading = true;
      });
    }

  }

  get modalDeviceDetailsTitle(): string {
    if (this.selectedDevice?.alias) {
      return `Detalhes do Dispositivo - ${this.selectedDevice.alias}`;
    }
    return 'Detalhes do Dispositivo';
  }

  viewDeviceLogs(item: any) {
    this.selectedDeviceForLogs = item;
    // Reseta tudo para garantir paginação limpa
    this.deviceLogs = [];
    this.deviceLogsPage = 1;
    this.deviceLogsHasMore = false;
    this.deviceLogsDeviceId = '';
    this.deviceLogDateRange = null;
    this.deviceLogsSearchText = '';
    this.isHideLoadingDeviceLogs = false;
    this.setupDeviceLogsSearch();
    this.modalDeviceLogs.open();
    // Carrega os logs após abrir o modal
    this.loadDeviceLogs(item, true);
  }

  loadDeviceLogs(device: any, reset: boolean = true) {
    this.isHideLoadingDeviceLogs = false;

    // Se for reset, limpa tudo e reinicia a paginação
    if (reset) {
      this.deviceLogs = [];
      this.deviceLogsAll = [];
      this.deviceLogsPage = 1;
      this.deviceLogsHasMore = false;
      this.deviceLogsDeviceId = '';
      this.deviceLogDateRange = null;
      this.deviceLogsSearchText = '';
    }

    // Verifica se o dispositivo tem id
    const deviceId = device?.id || device?.Id;

    if (!deviceId) {
      this.poDialogService.alert({
        title: 'Aviso',
        message: 'Este dispositivo não possui um ID válido.'
      });
      this.isHideLoadingDeviceLogs = true;
      return;
    }

    // Armazena o deviceId para próximas requisições
    if (!this.deviceLogsDeviceId) {
      this.deviceLogsDeviceId = deviceId;
    }

    // Se já temos o deviceId e não é reset, faz a requisição diretamente
    if (this.deviceLogsDeviceId && !reset) {
      this.loadMoreDeviceLogs();
      return;
    }

    // Busca os logs do dispositivo usando o deviceId com paginação
    this.loadMoreDeviceLogs();
  }

  loadMoreDeviceLogs() {
    if (!this.deviceLogsDeviceId) {
      this.isHideLoadingDeviceLogs = true;
      return;
    }

    this.isHideLoadingDeviceLogs = false;

    // Se houver pesquisa, usa pageSize de 200, senão usa o padrão
    const currentPageSize = (this.deviceLogsSearchText && this.deviceLogsSearchText.trim()) ? 200 : this.deviceLogsPageSize;

    // Formata as datas se houver período selecionado
    let startDate: string | undefined;
    let endDate: string | undefined;

    if (this.deviceLogDateRange && this.deviceLogDateRange.start && this.deviceLogDateRange.end) {
      startDate = this.formatDateForAPI(this.deviceLogDateRange.start);
      endDate = this.formatDateForAPI(this.deviceLogDateRange.end);
    }

    // Prepara o search para enviar à API
    const searchTerm = (this.deviceLogsSearchText && this.deviceLogsSearchText.trim()) ? this.deviceLogsSearchText.trim() : undefined;

    // Busca os logs do dispositivo usando o deviceId com paginação
    let apiUrl = `${environment.apiUrl}/v1/device-logs?deviceId=${encodeURIComponent(this.deviceLogsDeviceId)}&page=${this.deviceLogsPage}&pageSize=${currentPageSize}`;
    if (startDate) {
      apiUrl += `&startDate=${encodeURIComponent(startDate)}`;
    }
    if (endDate) {
      apiUrl += `&endDate=${encodeURIComponent(endDate)}`;
    }
    if (searchTerm) {
      apiUrl += `&search=${encodeURIComponent(searchTerm)}`;
    }

    this.http.get<any>(apiUrl).subscribe({
      next: (response) => {
        // Verifica se a resposta tem estrutura paginada
        const logsData = response.items || response.data || (Array.isArray(response) ? response : []);

        // Determina se há mais itens
        let hasNext = false;
        if (response.hasNext !== undefined) {
          hasNext = response.hasNext;
        } else if (Array.isArray(response)) {
          hasNext = response.length === currentPageSize;
        } else if (response.items) {
          hasNext = response.items.length === currentPageSize;
        }

        // Mapeia os logs recebidos
        const mappedLogs = (logsData || []).map((log: any) => {
          // Mapeia isError para Tipo de Evento: 0 = Normal, 1 = Erro
          const isError = log.isError !== undefined ? Number(log.isError) : (log.IsError !== undefined ? Number(log.IsError) : 0);
          const eventType = isError === 1 ? 'Erro' : 'Normal';

          return {
            logDate: this.formatAccessDate(log.logDate || log.date || log.createdAt),
            eventType: eventType,
            description: log.description || log.message || 'N/A',
            details: log.details || log.description || log.message || log.fullMessage || 'Nenhum detalhe disponível.',
            status: log.status || 'N/A',
            userName: log.userName || log.user?.name || log.userName || 'N/A',
            userTag: log.userTag || log.tag || log.user?.tag || log.userTag || 'N/A'
          };
        });

        // Se houver pesquisa, substitui os logs, senão adiciona
        if (searchTerm) {
          // Se for pesquisa, substitui todos os logs (reset)
          this.deviceLogs = mappedLogs;
          this.deviceLogsAll = mappedLogs;
        } else {
          // Se não for pesquisa, adiciona normalmente (paginação)
          this.deviceLogsAll = [...this.deviceLogsAll, ...mappedLogs];
          this.deviceLogs = [...this.deviceLogsAll];
        }

        // Atualiza o controle de paginação
        this.deviceLogsHasMore = hasNext;

        // Incrementa a página APENAS se houver mais itens para carregar e não for pesquisa
        if (this.deviceLogsHasMore && !searchTerm) {
          this.deviceLogsPage++;
        }

        this.isHideLoadingDeviceLogs = true;
      },
      error: (err) => {
        console.error('Erro ao buscar logs do dispositivo:', err);
        this.isHideLoadingDeviceLogs = true;
        this.deviceLogs = [];
        this.poDialogService.alert({
          title: 'Erro',
          message: err.error?.message || err.message || 'Não foi possível carregar os registros de acesso do dispositivo.'
        });
      }
    });
  }

  readonly deviceLogsTableColumns: Array<PoTableColumn> = [
    { property: 'logDate', label: 'Data/Hora', width: '20%' },
    {
      property: 'eventType',
      label: 'Tipo de Evento',
      width: '20%',
      type: 'label',
      labels: [
        { value: 'Normal', color: 'color-10', label: 'Normal', textColor: 'white' },
        { value: 'Erro', color: 'color-07', label: 'Erro', textColor: 'white' }
      ]
    },
    { property: 'description', label: 'Descrição', width: '45%' },
    { property: 'actions', label: 'Detalhes', type: 'cellTemplate', width: '15%' }
  ];

  get modalDeviceLogsTitle(): string {
    const deviceAlias = this.selectedDeviceForLogs?.alias || this.selectedDeviceForLogs?.name || '';
    if (deviceAlias) {
      return `Registro de Acesso - ${deviceAlias}`;
    }
    return 'Registro de Acesso do Dispositivo';
  }

  onDeviceLogDateRangeChange() {
    // Quando o filtro de data mudar, reseta a página e recarrega os logs
    this.deviceLogs = [];
    this.deviceLogsPage = 1;
    this.deviceLogsHasMore = false;
    this.loadMoreDeviceLogs();
  }

  clearDeviceLogDateFilter() {
    // Limpa o filtro de data e recarrega os logs sem filtro
    this.deviceLogDateRange = null;
    this.deviceLogs = [];
    this.deviceLogsPage = 1;
    this.deviceLogsHasMore = false;
    this.loadMoreDeviceLogs();
  }

  private formatDateForAPI(date: Date | string): string {
    if (!date) return '';
    try {
      const d = typeof date === 'string' ? new Date(date) : date;
      // Usa UTC para evitar problemas de timezone
      // Formata como YYYY-MM-DD para o endpoint
      const year = d.getUTCFullYear();
      const month = String(d.getUTCMonth() + 1).padStart(2, '0');
      const day = String(d.getUTCDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    } catch {
      return '';
    }
  }

  private formatAccessDate(date: string | Date): string {
    if (!date) return 'N/A';
    try {
      const d = typeof date === 'string' ? new Date(date) : date;
      return d.toLocaleString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      });
    } catch {
      return String(date);
    }
  }

  showDeviceLogDetails(item: any) {
    const details = item.details || item.description || 'Nenhum detalhe disponível.';
    this.poDialogService.alert({
      title: 'Detalhes do Log',
      message: details,
      literals: { ok: 'Fechar' }
    });
  }

  onDeviceLogsSearchChange() {
    // Dispara a busca na API com debounce
    this.deviceLogsSearchSubject.next(this.deviceLogsSearchText);
  }

  onDeviceLogsSearchInput(event: any) {
    this.deviceLogsSearchText = event.target?.value || '';
    // Dispara a busca na API com debounce
    this.deviceLogsSearchSubject.next(this.deviceLogsSearchText);
  }

  onDeviceLogsSearchBlur() {
    // Dispara a busca na API com debounce
    this.deviceLogsSearchSubject.next(this.deviceLogsSearchText);
  }

  private setupDeviceLogsSearch() {
    // Cancela subscription anterior se existir
    if (this.deviceLogsSearchSubscription) {
      this.deviceLogsSearchSubscription.unsubscribe();
    }

    // Configura o debounce para busca na API
    this.deviceLogsSearchSubscription = this.deviceLogsSearchSubject.pipe(
      debounceTime(500),
      distinctUntilChanged()
    ).subscribe(searchTerm => {
      if (searchTerm && searchTerm.trim()) {
        this.searchDeviceLogsInAPI(searchTerm.trim());
      } else {
        // Se não há busca, recarrega todos os logs
        this.deviceLogs = [];
        this.deviceLogsAll = [];
        this.deviceLogsPage = 1;
        this.deviceLogsHasMore = false;
        this.loadMoreDeviceLogs();
      }
    });
  }

  private searchDeviceLogsInAPI(searchTerm: string) {
    if (!this.deviceLogsDeviceId) return;

    this.isHideLoadingDeviceLogs = false;
    this.deviceLogs = [];
    this.deviceLogsAll = [];
    this.deviceLogsPage = 1;
    this.deviceLogsHasMore = false;

    // Formata as datas se houver período selecionado
    let startDate: string | undefined;
    let endDate: string | undefined;

    if (this.deviceLogDateRange && this.deviceLogDateRange.start && this.deviceLogDateRange.end) {
      startDate = this.formatDateForAPI(this.deviceLogDateRange.start);
      endDate = this.formatDateForAPI(this.deviceLogDateRange.end);
    }

    // Busca os logs com o termo de pesquisa
    let apiUrl = `${environment.apiUrl}/v1/device-logs?deviceId=${encodeURIComponent(this.deviceLogsDeviceId)}&page=1&pageSize=200`;
    if (startDate) {
      apiUrl += `&startDate=${encodeURIComponent(startDate)}`;
    }
    if (endDate) {
      apiUrl += `&endDate=${encodeURIComponent(endDate)}`;
    }
    if (searchTerm) {
      apiUrl += `&search=${encodeURIComponent(searchTerm)}`;
    }

    this.http.get<any>(apiUrl).subscribe({
      next: (response) => {
        const logsData = response.items || response.data || (Array.isArray(response) ? response : []);

        // Mapeia os logs recebidos
        const mappedLogs = (logsData || []).map((log: any) => {
          const isError = log.isError !== undefined ? Number(log.isError) : (log.IsError !== undefined ? Number(log.IsError) : 0);
          const eventType = isError === 1 ? 'Erro' : 'Normal';

          return {
            logDate: this.formatAccessDate(log.logDate || log.date || log.createdAt),
            eventType: eventType,
            description: log.description || log.message || 'N/A',
            details: log.details || log.description || log.message || log.fullMessage || 'Nenhum detalhe disponível.',
            status: log.status || 'N/A',
            userName: log.userName || log.user?.name || log.userName || 'N/A',
            userTag: log.userTag || log.tag || log.user?.tag || log.userTag || 'N/A'
          };
        });

        this.deviceLogs = mappedLogs;
        this.deviceLogsAll = mappedLogs;
        this.deviceLogsHasMore = response.hasNext || false;
        this.isHideLoadingDeviceLogs = true;
      },
      error: () => {
        this.isHideLoadingDeviceLogs = true;
      }
    });
  }

  clearDeviceLogsSearch() {
    this.deviceLogsSearchText = '';
    // Reseta e recarrega os logs sem search
    this.deviceLogs = [];
    this.deviceLogsAll = [];
    this.deviceLogsPage = 1;
    this.deviceLogsHasMore = false;
    this.loadMoreDeviceLogs();
  }
}
